import { useState, useRef, useEffect, useCallback } from "react";

interface SharePostProps {
  title: string;
  slug: string;
}

export default function SharePost({ title, slug }: SharePostProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const url = `${window.location.origin}/blog/${slug}`;
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  const emailUrl = `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [url]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative inline-flex items-center">
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className="inline-flex items-center gap-1.5 font-body text-sm text-foreground-600 hover:text-foreground-900 transition-colors duration-300 cursor-pointer"
        aria-label="Share this post"
      >
        <i className="ri-share-forward-line text-base" />
        <span className="whitespace-nowrap">Share</span>
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute bottom-full left-0 mb-2 bg-background-50 rounded-lg border border-background-200/70 py-2 min-w-[180px] z-50"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
              setOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-body text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className={`text-base ${copied ? "ri-check-line text-accent-600" : "ri-file-copy-line"}`} />
            {copied ? "Copied!" : "Copy link"}
          </button>

          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-body text-foreground-700 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
          >
            <i className="ri-twitter-x-line text-base" />
            Share on X
          </a>

          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-body text-foreground-700 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
          >
            <i className="ri-facebook-line text-base" />
            Share on Facebook
          </a>

          <a
            href={emailUrl}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-body text-foreground-700 hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
          >
            <i className="ri-mail-line text-base" />
            Share via email
          </a>
        </div>
      )}
    </div>
  );
}